import { Box, CircularProgress } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiError, DetailView } from '@pro4-erp/core-web';
import { useTranslation } from 'react-i18next';
import { ClientVisitDetail } from '../types';
import { getClientVisitById } from '../api/getClientVisitById';
import Details from '../components/Details';
import Reports from '../components/Reports';
import TeamMembers from '../components/TeamMembers';
import Answers from '../components/Answers';

const DetailPage = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>() as { id: string };

  const navigate = useNavigate();

  const query = useQuery<ClientVisitDetail, ApiError>(
    ['client-visits-id', id],
    () => getClientVisitById(id, i18n.language),
    { refetchOnWindowFocus: false }
  );

  if (query.isLoading) {
    return (
      <Box>
        <Box
          sx={{
            width: '100vw',
            height: '100vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CircularProgress size={100} />
        </Box>
      </Box>
    );
  }
  if (query.isError) {
    return <p>{query.error.message}</p>;
  }

  return (
    <Box sx={{ mt: 1 }}>
      <DetailView
        childrenSx={{ padding: '2rem' }}
        title={
          <DetailView.Title>{`${t('WO', { defaultValue: 'WO' })}: ${query.data.uId || 'N/A'} ${
            query.data.workOrder.name
          }`}</DetailView.Title>
        }
        onGoBack={() => navigate('/client-visits')}
        tabs={[
          { id: 'details', title: t('Details', { defaultValue: 'Details' }) },
          { id: 'teamMembers', title: t('Team members', { defaultValue: 'Team members' }) },
          { id: 'reports', title: t('Reports', { defaultValue: 'Reports' }) },
          { id: 'answers', title: t('Answers', { defaultValue: 'Answers' }) },
        ]}
        defaultTab="details"
      >
        {({ TabContent }) => (
          <>
            <TabContent tab="details">
              <Details clientVisitDetail={query.data} />
            </TabContent>
            <TabContent tab="reports">
              <Reports reports={query.data.reports || []} visitId={query.data.id} />
            </TabContent>
            <TabContent tab="answers">
              <Answers
                visitId={query.data.id}
                answers={query.data.answers || []}
                hasRecomendations={!!(query.data.recommend && Number(query.data.recommend) > 0)}
                hasNonConform={!!(query.data.nonConform && Number(query.data.nonConform) > 0)}
              />
            </TabContent>
            <TabContent tab="teamMembers">
              <TeamMembers members={query.data.teamMembers} />
            </TabContent>
          </>
        )}
      </DetailView>
    </Box>
  );
};

export default DetailPage;
